/** Reglas puras de la bandeja de Registros recibidos (gate de #97):
 * - "Cobrable" excluye estados terminales: cancelados, reembolsados y bajas
 *   NO son deuda ni entran al cobro masivo (hallazgo 4).
 * - La selección se reconcilia contra lo visible y cobrable, para que el
 *   contador del botón nunca opere sobre un lote distinto (hallazgo 5).
 * - El lote reporta éxitos y fallos REALES; los fallidos se conservan para
 *   reintentar (hallazgo 1).
 */

import { registrationDocumentReceived } from "./registrationDocumentLinks";

export type CollectableOrderLike = {
  status: string;
  participant?: { current_status?: string | null; participant_kind?: string | null } | null;
};

export function registrationOrderCollectable(order: CollectableOrderLike): boolean {
  // Gate #155: el cobro es POR JUGADOR — una orden anclada a una pareja
  // jamás es cobrable (ni checkbox, ni lote, ni conteo de pendientes);
  // solo se audita o se cancela. El backend rebota paid con 409
  // team_order_not_payable como red de fondo.
  if (order.participant?.participant_kind === "team") return false;
  return order.status === "pending" && order.participant?.current_status !== "dropped";
}

/** Poda la selección a los ids visibles y cobrables. Devuelve el MISMO Set si
 * nada cambió (evita re-renders en cascada). */
export function reconcileOrderSelection(
  selected: Set<number>,
  visibleCollectableIds: Set<number>,
): Set<number> {
  const next = new Set([...selected].filter((id) => visibleCollectableIds.has(id)));
  return next.size === selected.size ? selected : next;
}

/** Cierre del lote (carrera filtros↔lote): los fallidos solo permanecen
 * seleccionados si SIGUEN en la selección vigente — si un filtro o el usuario
 * los sacó mientras corría el lote, no se reintroducen. */
export function retainFailedSelection(
  failedIds: Iterable<number>,
  currentSelection: Set<number>,
): Set<number> {
  return new Set([...failedIds].filter((id) => currentSelection.has(id)));
}

/** Un pago puede cubrir varias categorías (caso Oscar Zajiontz, torneo 67):
 * una orden anclada a un participante y un renglón por categoría. Estas reglas
 * solo LEEN la orden; el cobro no depende de ellas. */
export type CoverageOrderLike = {
  id?: number;
  status: string;
  participant_id: number;
  participant?: {
    id?: number;
    division_id?: number | null;
    division?: { name?: string | null } | null;
    current_status?: string | null;
    participant_kind?: string | null;
  } | null;
  items?: Array<{
    participant_id: number;
    division_id?: number | null;
    division_name?: string | null;
    participant_status?: string | null;
  }> | null;
};

export type CoverageOptions = {
  /** Con quién juega el jugador esa categoría en pareja, si la pareja sigue
   * viva: al formarla, su fila individual de dobles queda de baja por diseño
   * (no es un rechazo). La página lo sabe por la lista de parejas. */
  partnerNameFor?: (divisionId: number | null) => string | null;
  /** Qué orden de la lista cuenta a ese participante (revisión ronda 2): un
   * participante puede quedar como renglón de dos órdenes (p. ej. una
   * rechazada y su nueva inscripción). Ver `coverageOwnerByParticipant`. */
  ownerOrderIdFor?: (participantId: number) => number | null;
};

/** Cada participante cuenta una sola vez en la bandeja (revisión ronda 2):
 * la orden anclada a él siempre gana (el ancla se cuenta como siempre); si
 * nadie lo ancla, la orden pendiente o pagada antes que la cancelada o
 * reembolsada, y entre iguales la más reciente. Solo lectura. */
export function coverageOwnerByParticipant(orders: CoverageOrderLike[]): Map<number, number> {
  const owners = new Map<number, number>();
  const anchored = new Set<number>();
  for (const order of orders) {
    const anchorId = order.participant?.id ?? order.participant_id;
    anchored.add(anchorId);
    if (order.id !== undefined && !owners.has(anchorId)) owners.set(anchorId, order.id);
  }
  const live = (order: CoverageOrderLike) => order.status === "pending" || order.status === "paid";
  const best = new Map<number, CoverageOrderLike>();
  for (const order of orders) {
    if (order.id === undefined) continue;
    for (const item of order.items ?? []) {
      if (anchored.has(item.participant_id)) continue;
      const current = best.get(item.participant_id);
      if (
        !current
        || (live(order) && !live(current))
        || (live(order) === live(current) && (order.id ?? 0) > (current.id ?? 0))
      ) {
        best.set(item.participant_id, order);
      }
    }
  }
  best.forEach((order, participantId) => owners.set(participantId, order.id as number));
  return owners;
}

export type OtherCategory = {
  participantId: number;
  divisionId: number | null;
  name: string;
  status: string | null;
  statusLabel: string;
  /** Fila de dobles reemplazada por una pareja viva: sigue jugando ahí. */
  partnerName: string | null;
};

export type CoveredCategory = {
  key: number | "none";
  name: string;
  /** El jugador sigue inscrito ahí (su fila no está de baja). */
  live: boolean;
  /** «Deben» en esa categoría: orden pendiente, no es de pareja y la
   * categoría sigue viva. */
  owed: boolean;
};

export function coverageStatusLabel(status: string | null | undefined): string {
  if (status === "pre_registered") return "Pre-registrado";
  if (status === "active" || status === "confirmed") return "Activo";
  if (status === "dropped") return "De baja";
  return "Sin estado";
}

/** Las OTRAS categorías que paga la misma orden (renglones que no son el
 * ancla), bajas incluidas, en el orden de la orden. Con `ownerOrderIdFor`,
 * sin los renglones que cuenta otra orden de la lista. */
export function orderOtherCategories(order: CoverageOrderLike, options: CoverageOptions = {}): OtherCategory[] {
  const anchorId = order.participant?.id ?? order.participant_id;
  return (order.items ?? [])
    .filter((item) => item.participant_id !== anchorId)
    .filter((item) => {
      if (!options.ownerOrderIdFor || order.id === undefined) return true;
      const owner = options.ownerOrderIdFor(item.participant_id);
      return owner === null || owner === order.id;
    })
    .map((item) => {
      const divisionId = item.division_id ?? null;
      const partnerName = item.participant_status === "dropped"
        ? options.partnerNameFor?.(divisionId) ?? null
        : null;
      return {
        participantId: item.participant_id,
        divisionId,
        name: item.division_name ?? "Sin categoría",
        status: item.participant_status ?? null,
        statusLabel: partnerName ? `En pareja con ${partnerName}` : coverageStatusLabel(item.participant_status),
        partnerName,
      };
    });
}

/** Categorías donde la fila cuenta y aparece al filtrar: la del ancla (como
 * siempre) más cada otra categoría que no esté de baja o que juega en pareja
 * (igual que una ancla con pareja). Sin repetir. «Deben» se decide por
 * categoría: dar de baja el ancla no apaga la deuda de otra categoría viva. */
export function orderCoveredCategories(order: CoverageOrderLike, options: CoverageOptions = {}): CoveredCategory[] {
  const collectable = order.status === "pending" && order.participant?.participant_kind !== "team";
  const anchorLive = order.participant?.current_status !== "dropped";
  const covered: CoveredCategory[] = [{
    key: order.participant?.division_id ?? "none",
    name: order.participant?.division?.name ?? "Sin categoría",
    live: anchorLive,
    owed: collectable && anchorLive,
  }];
  for (const other of orderOtherCategories(order, options)) {
    const live = other.status !== "dropped";
    if (!live && !other.partnerName) continue;
    const key = other.divisionId ?? "none";
    const existing = covered.find((entry) => entry.key === key);
    if (existing) {
      existing.live = existing.live || live;
      existing.owed = existing.owed || (collectable && live);
    } else {
      covered.push({ key, name: other.name, live, owed: collectable && live });
    }
  }
  return covered;
}

/** La orden aparece en «Pendientes de pago» si alguna categoría que cubre
 * debe. Solo pantalla: el cobro en lote y el recordatorio siguen usando
 * `registrationOrderCollectable` (el monto incluye las categorías de baja). */
export function registrationOrderOwes(order: CoverageOrderLike, options: CoverageOptions = {}): boolean {
  return orderCoveredCategories(order, options).some((entry) => entry.owed);
}

/** Otras categorías donde el jugador sigue jugando con este pago: vivas o
 * en pareja. Con alguna, dar de baja el ancla no lo saca del torneo ni es
 * un rechazo. */
export function orderInPlayOtherCategories(order: CoverageOrderLike, options: CoverageOptions = {}): OtherCategory[] {
  return orderOtherCategories(order, options).filter((other) => other.status !== "dropped" || other.partnerName);
}

/** Otras categorías que «Aprobar» puede aprobar: pre-registradas, y solo si
 * la orden sigue pendiente o pagada (nunca cancelada ni reembolsada). */
export function orderApprovableOtherCategories(order: CoverageOrderLike, options: CoverageOptions = {}): OtherCategory[] {
  if (order.status !== "pending" && order.status !== "paid") return [];
  return orderOtherCategories(order, options).filter((other) => other.status === "pre_registered");
}

/** Otras categorías vivas que «Eliminar jugador» ofrece dar de baja también. */
export function orderLiveOtherCategories(order: CoverageOrderLike, options: CoverageOptions = {}): OtherCategory[] {
  return orderOtherCategories(order, options).filter((other) => other.status !== "dropped");
}

/** Otras categorías de baja que «Reincorporar jugador» ofrece regresar
 * también: solo con la orden pendiente o pagada y nunca la fila de dobles que
 * reemplazó una pareja viva (reactivarla duplicaría al jugador). */
export function orderReinstatableOtherCategories(order: CoverageOrderLike, options: CoverageOptions = {}): OtherCategory[] {
  if (order.status !== "pending" && order.status !== "paid") return [];
  return orderOtherCategories(order, options).filter((other) => other.status === "dropped" && !other.partnerName);
}

export type TeamScopableOrderLike = {
  participant?: { participant_kind?: string | null } | null;
};

/** El pago y la inscripción son POR JUGADOR (decisión de producto, caso t54):
 * la bandeja lista solo participantes individuales — la fila de la pareja
 * triplicaba registros. La orden anclada a un equipo se oculta tras un toggle
 * explícito para poder auditarla o cancelarla; la pareja del jugador sigue
 * visible en su detalle expandido. Una orden SIN participante se conserva
 * visible: es una anomalía que el admin debe ver. */
export function isTeamAnchoredOrder(order: TeamScopableOrderLike): boolean {
  return order.participant?.participant_kind === "team";
}

export function scopeOrdersToIndividuals<T extends TeamScopableOrderLike>(
  orders: T[],
  showTeamOrders: boolean,
): T[] {
  return showTeamOrders ? orders : orders.filter((order) => !isTeamAnchoredOrder(order));
}

export type TeamLayoutRowInput = {
  order: {
    status: string;
    notes?: string | null;
    participant?: {
      division_id?: number | null;
      registration_documents?: { payment_receipt_url?: string | null; has_payment_receipt?: boolean } | null;
    } | null;
  };
  /** Celda categoria_nueva cruda del CSV. */
  categoryInput: string;
  /** Resolución de la celda: undefined = vacía, null = no encontrada, número = id. */
  categoryResolvedId: number | null | undefined;
  /** paymentStatusFromLayout(estatus_pago): null = celda vacía/ilegible. */
  statusParsed: string | null;
  /** Celda notas_pago cruda. */
  notesInput: string;
  /** Celda comprobante_admin ya normalizada (minúsculas). */
  receiptInput: string;
};

/** Decisión por fila de PAREJA del layout (gate #155, 4ª ronda): cada celda
 * editable se compara contra el estado ACTUAL de la orden — el CSV exportado
 * viene pre-llenado, así que "celda con valor" NO es "cambio". Fila sin
 * cambios → ignore (no se reporta como bloqueada); cualquier cambio distinto
 * de debe→rechazado → block; pending→cancelled con todo lo demás igual →
 * cancel, y el caller DEBE conservar notes = order.notes (jamás el texto del
 * CSV: el contrato es solo cancelación, sin cambios colaterales). */
export function teamLayoutRowDecision(input: TeamLayoutRowInput): "ignore" | "block" | "cancel" {
  const currentDivisionId = input.order.participant?.division_id ?? null;
  const categoryChanged =
    input.categoryInput.trim() !== ""
    && !(typeof input.categoryResolvedId === "number" && input.categoryResolvedId === currentDivisionId);
  const currentNotes = (input.order.notes ?? "").trim();
  const nextNotes = input.notesInput.trim();
  const notesChanged = nextNotes !== "" && nextNotes !== currentNotes;
  const receipt = input.receiptInput.trim();
  const receiptTruthy = ["si", "sí", "yes", "1", "true", "x"].includes(receipt);
  const currentReceipt = registrationDocumentReceived(input.order.participant?.registration_documents, "payment_receipt");
  const receiptChanged = receipt !== "" && receiptTruthy !== currentReceipt;
  if (categoryChanged || notesChanged || receiptChanged) return "block";
  const statusChanged = input.statusParsed !== null && input.statusParsed !== input.order.status;
  if (!statusChanged) return "ignore";
  return input.statusParsed === "cancelled" && input.order.status === "pending" ? "cancel" : "block";
}

export type BulkMarkPaidResult<T> = { succeeded: T[]; failed: T[] };

/** Corre el lote secuencialmente y contabiliza resultados reales — un fallo
 * individual NO detiene el resto ni se reporta como éxito. */
export async function runBulkMarkPaid<T>(
  targets: T[],
  markOne: (target: T) => Promise<void>,
): Promise<BulkMarkPaidResult<T>> {
  const succeeded: T[] = [];
  const failed: T[] = [];
  for (const target of targets) {
    try {
      await markOne(target);
      succeeded.push(target);
    } catch {
      failed.push(target);
    }
  }
  return { succeeded, failed };
}

/** Caso Tecamachalco (2026-10-27): la bienvenida con usuario y contraseña
 * sale al aprobar. Una orden «espera aprobación» EXACTAMENTE cuando la
 * bandeja le muestra un botón de aprobar (columna de acciones de
 * AdminTournamentRegistrationsPage): «Activar» (cobro del admin ya pagado con
 * el jugador preinscrito), «Aprobar» (jugador principal preinscrito) o
 * «Aprobar [otra categoría]» (principal ya activo o de baja y otra categoría
 * que cubre la misma orden sigue preinscrita: `orderApprovableOtherCategories`,
 * revisión de Torni al PR #593). Fuera: órdenes de pareja y cobros del admin
 * que el jugador aún no paga (esos se cobran, no se aprueban). Por orden, no
 * por categoría: una orden con varias pendientes cuenta una vez. Mismo
 * criterio que `order_awaiting_approval` del backend. */
export type ApprovalOrderLike = CoverageOrderLike & {
  participant?: {
    current_status?: string | null;
    participant_kind?: string | null;
    registration_source?: string | null;
  } | null;
};

export function registrationAwaitingApproval(order: ApprovalOrderLike, options: CoverageOptions = {}): boolean {
  const participant = order.participant;
  if (!participant || participant.participant_kind === "team") return false;
  const anchorPending = participant.current_status === "pre_registered";
  if (participant.registration_source === "admin_charge" && anchorPending) return order.status === "paid";
  if (anchorPending) return true;
  return orderApprovableOtherCategories(order, options).length > 0;
}

/** Conteo del aviso: cada orden una vez, con el mismo dueño de renglones que
 * la bandeja (`coverageOwnerByParticipant`), así N del aviso == filas de
 * «Ver pendientes». */
export function pendingApprovalCounts(orders: ApprovalOrderLike[]): { total: number; paid: number } {
  const owners = coverageOwnerByParticipant(orders);
  const options: CoverageOptions = { ownerOrderIdFor: (participantId) => owners.get(participantId) ?? null };
  const waiting = orders.filter((order) => registrationAwaitingApproval(order, options));
  return { total: waiting.length, paid: waiting.filter((order) => order.status === "paid").length };
}

/** Texto del aviso: «Tienes N inscripciones sin aprobar; M ya tienen pago confirmado». */
export function pendingApprovalHeadline(counts: { total: number; paid: number }): string {
  const { total, paid } = counts;
  const base = total === 1 ? "Tienes 1 inscripción sin aprobar" : `Tienes ${total} inscripciones sin aprobar`;
  if (paid <= 0) return `${base}.`;
  if (paid === total) return total === 1 ? `${base} y ya tiene pago confirmado.` : `${base} y todas tienen pago confirmado.`;
  return `${base}; ${paid} ya ${paid === 1 ? "tiene" : "tienen"} pago confirmado: atiéndelas primero.`;
}

/** Segunda línea: qué pierde el jugador mientras no se aprueba. */
export function pendingApprovalExplanation(autoSendWelcome: boolean): string {
  return autoSendWelcome
    ? "Los jugadores no reciben su usuario y contraseña hasta que las apruebes."
    : "Los jugadores no reciben su usuario y contraseña hasta que las apruebes y les envíes la bienvenida (la automática está apagada en Mensajes).";
}
